import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { firebaseApp } from "../../firebase";
import {
  getFirestore,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import {
  BookOpen,
  CheckCircle,
  Code,
  Lightbulb,
  Clock,
  Loader,
  ChevronRight,
  ChevronDown,
  Award,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import { getCourseFallback } from "../../utils/courseLookup";

const PersonalizedCourse = () => {
  const { courseId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const db = getFirestore(firebaseApp);

  const [loading, setLoading] = useState(true);
  const [courseData, setcourseData] = useState(null);
  const [expandedChapters, setExpandedChapters] = useState(
    new Set([1])
  );
  const [completedChapters, setCompletedChapters] = useState(
    new Set()
  );

  useEffect(() => {
    const fetchCourse = async () => {
      if (!courseId || !user) return;

      try {
        let courseData = null;
        let foundCourseId = courseId;

        // Attempt 1: Direct lookup by courseId (most common path)
        try {
          const courseDoc = await getDoc(doc(db, "personalizedCourses", courseId));
          if (courseDoc.exists()) {
            courseData = courseDoc.data();
            console.log("✅ Course found via direct lookup:", courseId);
          }
        } catch (directError) {
          console.warn("Direct courseId lookup failed:", directError);
        }

        // Attempt 2: Fallback lookup using courseLookup utilities
        if (!courseData) {
          console.log("🔄 Direct lookup failed, attempting fallback lookup...");
          const fallbackResult = await getCourseFallback(db, {
            courseId: courseId,
            userId: user.id,
          });

          if (fallbackResult) {
            courseData = fallbackResult.data;
            foundCourseId = fallbackResult.id;
            console.log("✅ Course found via fallback lookup:", foundCourseId);
          }
        }

        // Attempt 3: If still no course, show helpful error message
        if (!courseData) {
          console.error("Course not found after all lookup attempts");
          throw new Error(
            "Course not found. It may have been deleted or the course ID is invalid."
          );
        }

        setcourseData(courseData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching course:", error);
        alert(
          error.message ||
          "Failed to load course. Please try again or contact support."
        );
        navigate("/job-seeker");
      }
    };

    fetchCourse();
  }, [courseId, user, db, navigate]);

  const toggleChapter = (chapterNum) => {
    const newExpanded = new Set(expandedChapters);
    if (newExpanded.has(chapterNum)) {
      newExpanded.delete(chapterNum);
    } else {
      newExpanded.add(chapterNum);
    }
    setExpandedChapters(newExpanded);
  };

  const markChapterComplete = (chapterNum) => {
    setCompletedChapters(new Set([...completedChapters, chapterNum]));
  };

  const handleMarkCourseComplete = async () => {
    if (!courseId || !user) return;

    const confirmed = window.confirm(
      "Have you completed the entire course? You'll be taken to a test to verify your learning."
    );

    if (!confirmed) return;

    try {
      // Mark course 
      await updateDoc(doc(db, "personalizedCourses", courseId), {
        completed: true,
      });

      // NO points for just completing the course - points are awarded when passing the course test

      // Navigate to course test
      navigate(`/job-seeker/course-test/${courseId}`);
    } catch (error) {
      console.error("Error marking course complete:", error);
      alert("Failed to mark course ");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-red-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your personalized course...</p>
        </div>
      </div>
    );
  }

  if (!courseData || !courseData.courseData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Course not found</p>
          <button
            onClick={() => navigate("/job-seeker")}
            className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const course = courseData.courseData;
  const allChaptersCompleted =
    completedChapters.size === course.chapters.length;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Course Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg p-8 text-white mb-8">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <BookOpen className="w-8 h-8" />
                <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                  Personalized for You
                </span>
              </div>
              <h1 className="text-3xl font-bold mb-3">{course.courseTitle}</h1>
              <p className="text-blue-100 mb-4">{course.courseDescription}</p>
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4" />
                  <span>{course.estimatedDuration}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <BookOpen className="w-4 h-4" />
                  <span>{course.chapters.length} Chapters</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Award className="w-4 h-4" />
                  <span>10 points on completion</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Course Progress
            </span>
            <span className="text-sm font-medium text-gray-900">
              {completedChapters.size} / {course.chapters.length} chapters
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-600 h-2 rounded-full transition-all duration-300"
              style={{
                width: `${
                  (completedChapters.size / course.chapters.length) * 100
                }%`,
              }}
            />
          </div>
        </div>

        {/* Chapters */}
        <div className="space-y-4 mb-8">
          {course.chapters.map((chapter, index) => {
            const chapterNum = chapter.chapterNumber;
            const isExpanded = expandedChapters.has(chapterNum);
            const isCompleted = completedChapters.has(chapterNum);

            return (
              <div
                key={chapterNum}
                className="bg-white rounded-xl shadow-sm overflow-hidden"
              >
                {/* Chapter Header */}
                <button
                  onClick={() => toggleChapter(chapterNum)}
                  className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isCompleted
                          ? "bg-green-100 text-green-600"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {isCompleted ? (
                        <CheckCircle className="w-6 h-6" />
                      ) : (
                        <span className="font-bold">{chapterNum}</span>
                      )}
                    </div>
                    <div className="text-left">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {chapter.title}
                      </h3>
                      <p className="text-sm text-gray-600">{chapter.topic}</p>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="w-6 h-6 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-6 h-6 text-gray-400" />
                  )}
                </button>

                {/* Chapter Content */}
                {isExpanded && (
                  <div className="border-t border-gray-200 p-6 bg-gray-50">
                    {/* Chapter Text Content */}
                    <div className="prose max-w-none mb-6">
                      {chapter.paragraphs ? (
                        // New format: array of paragraphs
                        chapter.paragraphs.map((paragraph, idx) => (
                          <p key={idx} className="mb-4 text-gray-700 leading-relaxed">
                            {paragraph}
                          </p>
                        ))
                      ) : chapter.content ? (
                        // Old format: single content string (fallback)
                        <ReactMarkdown>{chapter.content}</ReactMarkdown>
                      ) : (
                        <p className="text-gray-500">No content available</p>
                      )}
                    </div>

                    {/* Code Examples */}
                    {chapter.codeExamples &&
                      chapter.codeExamples.length > 0 && (
                        <div className="mb-6">
                          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                            <Code className="w-5 h-5 text-blue-600" />
                            <span>Code Examples</span>
                          </h4>
                          <div className="space-y-4">
                            {chapter.codeExamples.map(
                              (example, idx) => (
                                <div
                                  key={idx}
                                  className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                                >
                                  <div className="bg-gray-800 text-gray-100 px-4 py-2 text-sm font-medium">
                                    {example.title}
                                  </div>
                                  <pre className="bg-gray-900 text-gray-100 p-4 overflow-x-auto">
                                    <code>{example.code}</code>
                                  </pre>
                                  <div className="px-4 py-3 bg-blue-50 border-t border-blue-200">
                                    <p className="text-sm text-gray-700">
                                      {example.explanation}
                                    </p>
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}

                    {/* Practice Exercises */}
                    {chapter.practiceExercises &&
                      chapter.practiceExercises.length > 0 && (
                        <div className="mb-6">
                          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                            <Lightbulb className="w-5 h-5 text-yellow-600" />
                            <span>Practice Exercises</span>
                          </h4>
                          <div className="space-y-4">
                            {chapter.practiceExercises.map(
                              (exercise, idx) => (
                                <div
                                  key={idx}
                                  className="bg-white rounded-lg border border-gray-200 p-4"
                                >
                                  <p className="font-medium text-gray-900 mb-2">
                                    Exercise {idx + 1}
                                  </p>
                                  <details className="mt-2">
                                    <summary className="text-sm text-blue-600 cursor-pointer hover:text-blue-700">
                                      Show Hint
                                    </summary>
                                    <p className="mt-2 text-sm text-gray-600 italic">
                                      {exercise.hint}
                                    </p>
                                  </details>
                                  <details className="mt-2">
                                    <summary className="text-sm text-green-600 cursor-pointer hover:text-green-700">
                                      Show Solution
                                    </summary>
                                    <pre className="mt-2 bg-gray-100 p-3 rounded text-sm overflow-x-auto">
                                      <code>{exercise.solution}</code>
                                    </pre>
                                  </details>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}

                    {/* Key Takeaways */}
                    {chapter.keyTakeaways && chapter.keyTakeaways.length > 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-3">
                          🎯 Key Takeaways
                        </h4>
                        <ul className="space-y-2">
                          {chapter.keyTakeaways.map(
                            (takeaway, idx) => (
                              <li
                                key={idx}
                                className="flex items-start space-x-2"
                              >
                                <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                                <span className="text-gray-700">{takeaway}</span>
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                    )}

                    {/* Mark Chapter Complete */}
                    {!isCompleted && (
                      <div className="mt-6 text-right">
                        <button
                          onClick={() => markChapterComplete(chapterNum)}
                          className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
                        >
                          Mark Chapter 
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Final Notes */}
        {course.finalNotes && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Final Notes & Encouragement
            </h3>
            <p className="text-gray-700 leading-relaxed">{course.finalNotes}</p>
          </div>
        )}

        {/* Complete Course Button */}
        <div className="bg-white rounded-xl shadow-sm p-6 text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Ready to Test Your Knowledge?
          </h3>
          <p className="text-gray-600 mb-6">
            Complete the course test to verify your learning and earn points!
          </p>
          <button
            onClick={handleMarkCourseComplete}
            disabled={!allChaptersCompleted}
            className={`px-8 py-4 rounded-lg font-semibold text-lg transition-all ${
              allChaptersCompleted
                ? "bg-red-600 text-white hover:bg-red-700 hover:shadow-lg"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            {allChaptersCompleted
              ? "Take Course Test (10 Questions)"
              : `Complete All Chapters First (${completedChapters.size}/${course.chapters.length})`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PersonalizedCourse;
